import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import Loader from 'components/Loader';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import SubSection from 'components/section-menu/SubSection';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { useRef } from 'react';
import { Control, FieldValues, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { useAddPracticeAreaMutation, useUpdatePracticeAreaMutation } from 'store/api/practice-areas';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { PracticeAreaForm } from 'types/practice-areas';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';
import { PracticeAreaFields, PracticeAreaVisibilityFields } from './form-sections/PracticeAreaFields';
import { usePracticeAreaForm } from './form-sections/usePracticeAreaForm';
import { practiceAreaFormToRequest } from './practiceAreaFormMapping';

type Props = {
    type: 'add' | 'edit';
    initialData?: PracticeAreaForm;
    classifiers: Record<string, ClassifierData>;
    refetch?: () => void;
    entityCustomFields?: CustomField[];
};

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    practiceAreaType: 'practice-area',
    name: 'practice-area',
    parent: 'practice-area',
    practiceGroup: 'practice-area',
    practiceTeams: 'practice-area',
    isAmlApplicable: 'practice-area',
    isExcludedFromAutomaticClosing: 'practice-area',
    applyExclusionToSubareas: 'practice-area',
    isActive: 'practice-area',
};

const PracticeAreaPageForm = ({ type, initialData, classifiers, refetch, entityCustomFields }: Props): JSX.Element => {
    useFocusedEditMode('practice-areas');
    const { id: practiceAreaId } = useCreateRoute().params;
    const returnTo = useReturnToLocation(practiceAreaId ? `/practice-area/${practiceAreaId}` : '/practice-areas');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    const form = usePracticeAreaForm({ initialData, classifiers, entityCustomFields });
    const { methods, customFields, mapCustomFieldsToDto, isLoadingCustomFieldData } = form;
    useCreateDialogForm(methods);
    const draft = useCreateDraft({
        record: 'practice-area',
        methods,
        toPayload: (data: PracticeAreaForm) =>
            practiceAreaFormToRequest(data, mapCustomFieldsToDto(data as unknown as Record<string, unknown>)),
    });

    const { handleSubmit, control, reset, formState } = methods;
    const { errors } = formState;

    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);

    const [addPracticeArea] = useAddPracticeAreaMutation();
    const [updatePracticeArea] = useUpdatePracticeAreaMutation();

    const bypassNavigationBlockerRef = useRef(false);

    const onSubmit = async (formData: PracticeAreaForm, action: 'create' | 'update'): Promise<void> => {
        try {
            const dataWithCustomFields = practiceAreaFormToRequest(
                formData,
                mapCustomFieldsToDto(formData as unknown as Record<string, unknown>)
            );

            if (action === 'create') {
                // A draft is created by completing it: the practice area keeps the draft's id.
                await (
                    draft.draftId === null
                        ? addPracticeArea(dataWithCustomFields).unwrap()
                        : draft.complete(dataWithCustomFields)
                )
                    .then((resp) => {
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveForm(`/practice-area/${resp.id}`);
                    })
                    .catch(console.error);
            } else if (action === 'update' && practiceAreaId && !isNaN(Number(practiceAreaId))) {
                await updatePracticeArea({
                    ...dataWithCustomFields,
                    id: practiceAreaId,
                })
                    .unwrap()
                    .then(() => {
                        bypassNavigationBlockerRef.current = true;
                        if (refetch) refetch();
                        reset({}, { keepValues: true });
                        leaveForm(`/practice-area/${practiceAreaId}`);
                    })
                    .catch(console.error);
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitCreate = () => {
        handleSubmit((data) => onSubmit(data, 'create'))();
    };

    const submitUpdate = () => {
        handleSubmit((data) => onSubmit(data, 'update'))();
    };

    const { buttonsDisabled } = useSelector((state) => state.buttons);

    if (isLoadingCustomFieldData) {
        return <Loader />;
    }

    // Show inital name if is edit form. Dont do dynamically to prevent unneeded rerenders
    const getFormTitle = () => {
        switch (type) {
            case 'edit':
                return initialData?.name;
            default:
                return t('practice-areas.add-new-practice-area');
        }
    };

    const getFormHeader = () => {
        const actions: JSX.Element[] = [<CancelButton key="cancel" onClick={goBack} />];

        if (type === 'add') {
            actions.push(
                ...(draft.button ? [draft.button] : []),
                <SaveButton
                    key="addSave"
                    permissions={['CREATE.PRACTICE_AREAS']}
                    onClick={submitCreate}
                    disabled={buttonsDisabled}
                />
            );
        } else if (type === 'edit' && practiceAreaId) {
            actions.push(
                <PermissionButton
                    key="editSave"
                    permissions={['UPDATE.PRACTICE_AREAS']}
                    color="secondary"
                    variant="contained"
                    size="small"
                    onClick={submitUpdate}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="save" />
                </PermissionButton>
            );
        }

        return <ActionHeader title={getFormTitle()} actions={actions} onBack={goBack} draft={draft.detail} />;
    };

    return (
        <FormProvider {...methods}>
            <SectionActionsProvider>
                <HighlightedFieldsProvider>
                    <FormWrapper>
                        {getFormHeader()}
                        <DraftSaveMode mode={draft.mode}>
                            <SectionLayout showMenu={false} sectionErrors={sectionErrors}>
                                <Section title="practice-area">
                                    <SubSection title="practice-area">
                                        <PracticeAreaFields form={form} />
                                        <PracticeAreaVisibilityFields form={form} />
                                    </SubSection>
                                </Section>
                                {customFields?.length > 0 ? (
                                    <CustomFields
                                        control={control as unknown as Control<FieldValues>}
                                        fields={customFields}
                                    />
                                ) : null}
                            </SectionLayout>
                        </DraftSaveMode>
                    </FormWrapper>
                    <ConfirmNavigation
                        control={control}
                        bypassRef={bypassNavigationBlockerRef}
                        draft={type === 'add' ? draft.leave : null}
                    />
                </HighlightedFieldsProvider>
            </SectionActionsProvider>
        </FormProvider>
    );
};

export default PracticeAreaPageForm;
